import { useContext } from 'react';
import { AlertContext, AlertSequenceItem } from './AlertContext';

type Confirm = {
  message: string | React.ReactElement;
  description?: string | React.ReactElement;
  onConfirm?: () => void;
  onCancel?: () => void;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  hideCancellation?: boolean;
};

type Alert = {
  message: string | React.ReactElement;
  description?: string | React.ReactElement;
  onClose?: () => void;
  type?: 'wideAlert';
  confirmButtonLabel?: string;
  attentionMsg?: string;
};

export const useAlert = () => {
  const [alertState, setAlertState] = useContext(AlertContext);

  return {
    alert: ({
      message,
      description,
      onClose,
      type,
      confirmButtonLabel,
      attentionMsg,
    }: Alert) =>
      setAlertState({
        open: true,
        type: type ?? 'alert',
        message,
        description,
        onClose,
        confirmButtonLabel,
        attentionMsg,
        isSequenceActive: false,
      }),
    confirm: ({
      message,
      onConfirm,
      onCancel,
      description,
      confirmButtonLabel,
      cancelButtonLabel,
      hideCancellation,
    }: Confirm) =>
      setAlertState({
        open: true,
        type: 'confirm',
        message,
        onConfirm,
        onCancel,
        description,
        cancelButtonLabel,
        confirmButtonLabel,
        hideCancellation,
        isSequenceActive: false,
      }),
    close: () =>
      setAlertState((prev) => ({
        ...prev,
        open: false,
        isSequenceActive: false,
      })),
    alertState,
    sequence: (alerts: AlertSequenceItem[]) => {
      if (!alerts.length) return () => {};

      let isCancelled = false;

      setAlertState((prev) => ({ ...prev, isSequenceActive: true }));

      const showNext = (index: number) => {
        if (isCancelled) return;
        if (index >= alerts.length) {
          setTimeout(() => {
            if (isCancelled) return;
            setAlertState((prev) => ({ ...prev, isSequenceActive: false }));
          }, 200);
          return;
        }
        setAlertState({
          ...alerts[index],
          open: true,
          isSequenceActive: true,
          onSequenceClose: () => {
            setTimeout(() => showNext(index + 1), 100);
          },
        });
      };

      setTimeout(() => showNext(0), 0);

      return () => {
        isCancelled = true;
        setAlertState((prev) =>
          prev.isSequenceActive
            ? { ...prev, open: false, isSequenceActive: false }
            : prev
        );
      };
    },
  };
};
